import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form } from "react-bootstrap";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import { fetchClients } from "../../api/overviewApi";
import { useNavigate } from "react-router-dom";

import { FaUser, FaEnvelope, FaPhone, FaShieldAlt, FaClock, FaGlobe, FaLock, FaMapMarkerAlt, FaCalendar, FaTimes } from "react-icons/fa";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: "easeOut" },
};

export default function UserProfilePage() {
  const { user: authUser, isStaffUser } = useAuth();
  const navigate = useNavigate();
  const [clientName, setClientName] = useState(null);
  const [clients, setClients] = useState([]);
  
  // Fetch clients list to get client name by client_id
  useEffect(() => {
    const fetchClientName = async () => {
      try {
        const clientsList = await fetchClients();
        setClients(clientsList);
        
        // Get client_id from user
        const clientId = authUser?.client?.client_id || authUser?.client_id;
        
        if (clientId && clientsList.length > 0) {
          const foundClient = clientsList.find(c => c.client_id === clientId);
          if (foundClient?.client_name) {
            setClientName(foundClient.client_name);
          }
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    
    fetchClientName();
  }, [authUser]);
  
  // Determine the client name to display
  const displayClientName = 
    authUser?.client?.client_name || 
    clientName || 
    null;
  
  // Get user data from auth context or use defaults
  const user = {
    name: displayClientName || authUser?.full_name || (authUser?.first_name + " " + (authUser?.last_name || "")).trim() || "User",
    email: authUser?.email || "N/A",
    phone: "+1 (555) 123-4567", // Not in user model, keep default
    location: "United States", // Not in user model, keep default
    role: authUser?.is_superuser ? "Super Admin" : authUser?.is_staff ? "Staff" : "Normal User",
    memberSince: authUser?.date_joined ? new Date(authUser.date_joined).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : "N/A",
    timezone: "America/New_York (EST)", // Not in user model, keep default
    lastLogin: authUser?.last_login ? new Date(authUser.last_login).toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    }) : "N/A",
    avatar: "/static/images/profile.png",
    clientName: displayClientName,
  };

  const handleClose = () => {
    // Navigate back to previous page or default to overview
    const isStaff = isStaffUser();
    const defaultPath = isStaff ? "/superadmin/overview" : "/client/overview";
    navigate(-1); // Go back to previous page, or fallback to default
  };

  return (
    <motion.div className="p-3" initial="initial" animate="animate" variants={{ animate: { transition: { staggerChildren: 0.12 } } }} style={{ position: "relative" }}>
      {/* CLOSE BUTTON */}
      <button
        onClick={handleClose}
        style={{
          position: "absolute",
          top: "12px",
          right: "12px",
          width: "40px",
          height: "40px",
          borderRadius: "12px",
          border: "1px solid var(--color-border)",
          background: "var(--color-bg-card)",
          color: "var(--color-text-light)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.25s ease",
          zIndex: 10,
          boxShadow: "var(--shadow-sm)",
        }}
        onMouseEnter={(e) => {
          e.target.style.background = "var(--color-primary-light)";
          e.target.style.color = "var(--color-primary)";
          e.target.style.borderColor = "var(--color-primary)";
        }}
        onMouseLeave={(e) => {
          e.target.style.background = "var(--color-bg-card)";
          e.target.style.color = "var(--color-text-light)";
          e.target.style.borderColor = "var(--color-border)";
        }}
      >
        <FaTimes size={18} />
      </button>

      {/* PAGE TITLE */}
      <motion.h3 variants={fadeIn} className="fw-bold mb-4" style={{ color: "var(--color-text)" }}>
        User Profile
      </motion.h3>

      <Row>
        {/* LEFT: PROFILE CARD */}
        <Col lg={4} className="mb-4">
          <motion.div variants={fadeIn}>
            <Card
              className="p-0"
              style={{
                borderRadius: 20,
                overflow: "hidden",
                position: "relative",
              }}
            >
              {/* Gradient header */}
              <div
                style={{
                  height: 120,
                  background: "linear-gradient(135deg, var(--color-primary), #a78bfa)",
                }}
              />

              {/* Avatar */}
              <div className="text-center" style={{ marginTop: -60 }}>
                <img
                  src={user.avatar}
                  alt={user.name}
                  onError={(e) => {
                    // Fallback to default avatar if /static/images/profile.png fails to load
                    e.target.src = "https://i.pravatar.cc/300?img=68";
                  }}
                  style={{
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    border: "4px solid var(--color-bg-card)",
                    boxShadow: "var(--shadow-md)",
                    objectFit: "cover",
                  }}
                />
                <h5 className="mt-3 fw-semibold">{user.name}</h5>
                <div
                  className="px-3 py-1 mt-1"
                  style={{
                    fontSize: 13,
                    borderRadius: 8,
                    display: "inline-block",
                    background: "var(--color-primary-light)",
                    color: "var(--color-primary)",
                    fontWeight: 600,
                  }}
                >
                  {user.role}
                </div>
                {user.clientName && (
                  <div
                    className="px-3 py-1 mt-2"
                    style={{
                      fontSize: 12,
                      borderRadius: 8,
                      display: "inline-block",
                      background: "rgba(59, 130, 246, 0.1)",
                      color: "#3b82f6",
                      fontWeight: 500,
                    }}
                  >
                    {user.clientName}
                  </div>
                )}
              </div>

              <Card.Body className="mt-3">
                <div className="d-flex align-items-start gap-3 mb-2" style={{ minHeight: "24px" }}>
                  <FaEnvelope size={16} color="var(--color-text-light)" style={{ marginTop: "2px", flexShrink: 0 }} />
                  <span style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>{user.email}</span>
                </div>
                <div className="d-flex align-items-center gap-3 mb-2">
                  <FaPhone size={16} color="var(--color-text-light)" style={{ flexShrink: 0 }} />
                  <span>{user.phone}</span>
                </div>
                <div className="d-flex align-items-center gap-3 mb-2">
                  <FaMapMarkerAlt size={16} color="var(--color-text-light)" style={{ flexShrink: 0 }} />
                  <span>{user.location}</span>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <FaCalendar size={16} color="var(--color-text-light)" style={{ flexShrink: 0 }} />
                  <span>Member Since: {user.memberSince}</span>
                </div>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>

        {/* RIGHT: DETAILS + SETTINGS */}
        <Col lg={8}>
          <Row>
            {/* ACCOUNT DETAILS */}
            <Col md={12} className="mb-4">
              <motion.div variants={fadeIn}>
                <Card className="p-4">
                  <h5 className="fw-semibold mb-3">Account Details</h5>

                  <Row className="mb-3">
                    <Col md={6}>
                      <div className="text-light">Full Name</div>
                      <div className="fw-semibold">{user.name}</div>
                    </Col>
                    <Col md={6}>
                      <div className="text-light">Email Address</div>
                      <div className="fw-semibold">{user.email}</div>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <div className="text-light">Role</div>
                      <div className="fw-semibold">{user.role}</div>
                    </Col>
                    {user.clientName && (
                      <Col md={6}>
                        <div className="text-light">Client</div>
                        <div className="fw-semibold">{user.clientName}</div>
                      </Col>
                    )}
                    {!user.clientName && (
                      <>
                        <Col md={6}>
                          <div className="text-light">Phone Number</div>
                          <div className="fw-semibold">{user.phone}</div>
                        </Col>
                      </>
                    )}
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="text-light">Member Since</div>
                      <div className="fw-semibold">{user.memberSince}</div>
                    </Col>
                    <Col md={6}>
                      <div className="text-light">Last Login</div>
                      <div className="fw-semibold">{user.lastLogin}</div>
                    </Col>
                  </Row>
                </Card>
              </motion.div>
            </Col>

            {/* SECURITY SETTINGS */}
            <Col md={12} className="mb-4">
              <motion.div variants={fadeIn}>
                <Card className="p-4">
                  <h5 className="fw-semibold mb-3">Security Settings</h5>

                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <FaShieldAlt color="var(--color-primary)" />
                      <span className="fw-semibold">Two-Factor Authentication</span>
                    </div>
                    <Form.Check type="switch" defaultChecked />
                  </div>

                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <FaLock color="var(--color-primary)" />
                      <span className="fw-semibold">Require Strong Passwords</span>
                    </div>
                    <Form.Check type="switch" defaultChecked />
                  </div>

                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      <FaGlobe color="var(--color-primary)" />
                      <span className="fw-semibold">Allow New Device Logins</span>
                    </div>
                    <Form.Check type="switch" />
                  </div>
                </Card>
              </motion.div>
            </Col>

            {/* ACTIVITY TIMELINE */}
            <Col md={12}>
              <motion.div variants={fadeIn}>
                <Card className="p-4">
                  <h5 className="fw-semibold mb-3">Recent Activity</h5>

                  <div className="d-flex flex-column gap-3">
                    {[
                      {
                        icon: <FaUser />,
                        text: "Profile updated",
                        time: "2 hours ago",
                      },
                      {
                        icon: <FaClock />,
                        text: "Security settings changed",
                        time: "Yesterday",
                      },
                      {
                        icon: <FaShieldAlt />,
                        text: "Logged in from new device",
                        time: "2 days ago",
                      },
                    ].map((item, i) => (
                      <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }} className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            style={{
                              width: 38,
                              height: 38,
                              borderRadius: "50%",
                              background: "var(--color-bg-card)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "var(--shadow-sm)",
                            }}
                          >
                            {item.icon}
                          </div>
                          <span>{item.text}</span>
                        </div>
                        <span className="text-light">{item.time}</span>
                      </motion.div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </Col>
      </Row>
    </motion.div>
  );
}
